<template>
  <div
    v-if="minimized"
    class="viewport-debug-panel viewport-debug-panel--minimized"
    role="region"
    aria-label="Diagnóstico de viewport minimizado"
  >
    <button
      type="button"
      class="viewport-debug-panel__button viewport-debug-panel__button--expand"
      aria-label="Expandir diagnóstico de viewport"
      @click="minimized = false"
    >
      Abrir diagnóstico
    </button>
  </div>

  <aside
    v-else
    class="viewport-debug-panel"
    role="region"
    aria-label="Diagnóstico de viewport"
  >
    <header class="viewport-debug-panel__header">
      <div>
        <h2 class="viewport-debug-panel__title">Diagnóstico de viewport</h2>
        <p class="viewport-debug-panel__subtitle">
          Leitura local do dispositivo
        </p>
      </div>

      <div class="viewport-debug-panel__header-actions">
        <button
          type="button"
          class="viewport-debug-panel__icon-button"
          aria-label="Minimizar diagnóstico"
          title="Minimizar"
          @click="minimized = true"
        >
          −
        </button>
        <button
          type="button"
          class="viewport-debug-panel__icon-button"
          aria-label="Fechar diagnóstico"
          title="Fechar"
          @click="close"
        >
          ×
        </button>
      </div>
    </header>

    <div class="viewport-debug-panel__actions">
      <button
        type="button"
        class="viewport-debug-panel__button"
        @click="updateMeasurements"
      >
        Atualizar
      </button>
      <button
        type="button"
        class="viewport-debug-panel__button viewport-debug-panel__button--secondary"
        @click="copyData"
      >
        Copiar dados
      </button>
    </div>

    <p
      v-if="feedbackMessage"
      class="viewport-debug-panel__feedback"
      :class="`viewport-debug-panel__feedback--${feedbackType}`"
      aria-live="polite"
    >
      {{ feedbackMessage }}
    </p>

    <div class="viewport-debug-panel__body">
      <section class="viewport-debug-panel__section">
        <h3>Tela física</h3>
        <dl>
          <dt>screen.width</dt>
          <dd>{{ measurements.screen.width }}</dd>
          <dt>screen.height</dt>
          <dd>{{ measurements.screen.height }}</dd>
          <dt>screen.availWidth</dt>
          <dd>{{ measurements.screen.availWidth }}</dd>
          <dt>screen.availHeight</dt>
          <dd>{{ measurements.screen.availHeight }}</dd>
          <dt>screen.colorDepth</dt>
          <dd>{{ measurements.screen.colorDepth }}</dd>
        </dl>
      </section>

      <section class="viewport-debug-panel__section">
        <h3>Viewport do navegador</h3>
        <dl>
          <dt>window.innerWidth</dt>
          <dd>{{ measurements.viewport.innerWidth }}</dd>
          <dt>window.innerHeight</dt>
          <dd>{{ measurements.viewport.innerHeight }}</dd>
          <dt>document.documentElement.clientWidth</dt>
          <dd>{{ measurements.viewport.clientWidth }}</dd>
          <dt>document.documentElement.clientHeight</dt>
          <dd>{{ measurements.viewport.clientHeight }}</dd>
        </dl>
      </section>

      <section class="viewport-debug-panel__section">
        <h3>Largura de rolagem</h3>
        <dl>
          <dt>document.documentElement.scrollWidth</dt>
          <dd>{{ measurements.overflow.documentElement }}</dd>
          <dt>document.body.scrollWidth</dt>
          <dd>{{ measurements.overflow.body }}</dd>
          <dt>#app.scrollWidth</dt>
          <dd>{{ measurements.overflow.app }}</dd>
        </dl>
      </section>

      <section class="viewport-debug-panel__section">
        <h3>Visual Viewport</h3>
        <template v-if="measurements.visualViewport.supported">
          <dl>
            <dt>visualViewport.width</dt>
            <dd>{{ measurements.visualViewport.width }}</dd>
            <dt>visualViewport.height</dt>
            <dd>{{ measurements.visualViewport.height }}</dd>
            <dt>visualViewport.scale</dt>
            <dd>{{ measurements.visualViewport.scale }}</dd>
            <dt>visualViewport.offsetTop</dt>
            <dd>{{ measurements.visualViewport.offsetTop }}</dd>
            <dt>visualViewport.offsetLeft</dt>
            <dd>{{ measurements.visualViewport.offsetLeft }}</dd>
            <dt>visualViewport.pageTop</dt>
            <dd>{{ measurements.visualViewport.pageTop }}</dd>
            <dt>visualViewport.pageLeft</dt>
            <dd>{{ measurements.visualViewport.pageLeft }}</dd>
          </dl>
        </template>
        <p v-else class="viewport-debug-panel__notice">
          Visual Viewport não suportada
        </p>
      </section>

      <section class="viewport-debug-panel__section">
        <h3>Dispositivo e escala</h3>
        <dl>
          <dt>window.devicePixelRatio</dt>
          <dd>{{ measurements.devicePixelRatio }}</dd>
          <dt>navigator.maxTouchPoints</dt>
          <dd>{{ measurements.maxTouchPoints }}</dd>
        </dl>
      </section>

      <section class="viewport-debug-panel__section">
        <h3>Media queries</h3>
        <dl>
          <dt>pointer: coarse</dt>
          <dd>{{ measurements.mediaQueries.pointerCoarse }}</dd>
          <dt>pointer: fine</dt>
          <dd>{{ measurements.mediaQueries.pointerFine }}</dd>
          <dt>hover: hover</dt>
          <dd>{{ measurements.mediaQueries.hover }}</dd>
          <dt>hover: none</dt>
          <dd>{{ measurements.mediaQueries.noHover }}</dd>
          <dt>orientation: portrait</dt>
          <dd>{{ measurements.mediaQueries.portrait }}</dd>
          <dt>orientation: landscape</dt>
          <dd>{{ measurements.mediaQueries.landscape }}</dd>
          <dt>display-mode: fullscreen</dt>
          <dd>{{ measurements.mediaQueries.fullscreen }}</dd>
          <dt>display-mode: standalone</dt>
          <dd>{{ measurements.mediaQueries.standalone }}</dd>
        </dl>
      </section>

      <section class="viewport-debug-panel__section">
        <h3>Orientação</h3>
        <dl v-if="measurements.orientation.supported">
          <dt>screen.orientation.type</dt>
          <dd>{{ measurements.orientation.type }}</dd>
          <dt>screen.orientation.angle</dt>
          <dd>{{ measurements.orientation.angle }}</dd>
        </dl>
        <p v-else class="viewport-debug-panel__notice">
          screen.orientation não está disponível
        </p>
      </section>

      <section class="viewport-debug-panel__section">
        <h3>Navegador</h3>
        <dl>
          <dt>navigator.userAgent</dt>
          <dd class="viewport-debug-panel__long-value">
            {{ measurements.browser.userAgent }}
          </dd>
          <dt>navigator.platform</dt>
          <dd>{{ measurements.browser.platform }}</dd>
          <dt>navigator.language</dt>
          <dd>{{ measurements.browser.language }}</dd>
          <dt>navigator.onLine</dt>
          <dd>{{ measurements.browser.onLine }}</dd>
        </dl>
      </section>
    </div>
  </aside>
</template>

<script>
const createEmptyMeasurements = () => ({
  screen: {
    width: "—",
    height: "—",
    availWidth: "—",
    availHeight: "—",
    colorDepth: "—",
  },
  viewport: {
    innerWidth: "—",
    innerHeight: "—",
    clientWidth: "—",
    clientHeight: "—",
  },
  overflow: {
    documentElement: "—",
    body: "—",
    app: "—",
  },
  visualViewport: {
    supported: false,
    width: "—",
    height: "—",
    scale: "—",
    offsetTop: "—",
    offsetLeft: "—",
    pageTop: "—",
    pageLeft: "—",
  },
  devicePixelRatio: "—",
  maxTouchPoints: "—",
  mediaQueries: {
    pointerCoarse: false,
    pointerFine: false,
    hover: false,
    noHover: false,
    portrait: false,
    landscape: false,
    fullscreen: false,
    standalone: false,
  },
  orientation: {
    supported: false,
    type: "—",
    angle: "—",
  },
  browser: {
    userAgent: "—",
    platform: "—",
    language: "—",
    onLine: false,
  },
});

export default {
  name: "ViewportDebugPanel",
  data() {
    return {
      measurements: createEmptyMeasurements(),
      minimized: false,
      feedbackMessage: "",
      feedbackType: "success",
      visualViewportTarget: null,
      updateFrame: null,
      updateFrameType: "",
    };
  },
  mounted() {
    this.bindListeners();
    this.updateMeasurements();
  },
  beforeDestroy() {
    this.unbindListeners();
    this.cancelScheduledUpdate();
  },
  methods: {
    bindListeners() {
      window.addEventListener("resize", this.scheduleUpdate, { passive: true });
      window.addEventListener("orientationchange", this.scheduleUpdate, {
        passive: true,
      });

      this.visualViewportTarget = window.visualViewport || null;
      if (this.visualViewportTarget) {
        this.visualViewportTarget.addEventListener(
          "resize",
          this.scheduleUpdate,
          { passive: true }
        );
        this.visualViewportTarget.addEventListener(
          "scroll",
          this.scheduleUpdate,
          { passive: true }
        );
      }
    },
    unbindListeners() {
      window.removeEventListener("resize", this.scheduleUpdate);
      window.removeEventListener("orientationchange", this.scheduleUpdate);

      if (this.visualViewportTarget) {
        this.visualViewportTarget.removeEventListener(
          "resize",
          this.scheduleUpdate
        );
        this.visualViewportTarget.removeEventListener(
          "scroll",
          this.scheduleUpdate
        );
      }
      this.visualViewportTarget = null;
    },
    scheduleUpdate() {
      if (this.updateFrame !== null) {
        return;
      }

      if (typeof window.requestAnimationFrame === "function") {
        this.updateFrameType = "animationFrame";
        this.updateFrame = window.requestAnimationFrame(() => {
          this.updateFrame = null;
          this.updateFrameType = "";
          this.updateMeasurements();
        });
        return;
      }

      this.updateFrameType = "timeout";
      this.updateFrame = window.setTimeout(() => {
        this.updateFrame = null;
        this.updateFrameType = "";
        this.updateMeasurements();
      }, 100);
    },
    cancelScheduledUpdate() {
      if (this.updateFrame === null) {
        return;
      }

      if (
        this.updateFrameType === "animationFrame" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(this.updateFrame);
      } else {
        window.clearTimeout(this.updateFrame);
      }

      this.updateFrame = null;
      this.updateFrameType = "";
    },
    getMediaQueryResult(query) {
      if (typeof window.matchMedia !== "function") {
        return false;
      }

      return Boolean(window.matchMedia(query).matches);
    },
    updateMeasurements() {
      const screen = window.screen || {};
      const visualViewport = window.visualViewport;
      const orientation = screen.orientation;
      const documentElement = document.documentElement || {};
      const body = document.body;
      const app = document.getElementById("app");

      this.measurements = {
        screen: {
          width: screen.width,
          height: screen.height,
          availWidth: screen.availWidth,
          availHeight: screen.availHeight,
          colorDepth: screen.colorDepth,
        },
        viewport: {
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
          clientWidth: documentElement.clientWidth,
          clientHeight: documentElement.clientHeight,
        },
        overflow: {
          documentElement: documentElement.scrollWidth,
          body: body ? body.scrollWidth : "—",
          app: app ? app.scrollWidth : "—",
        },
        visualViewport: visualViewport
          ? {
              supported: true,
              width: visualViewport.width,
              height: visualViewport.height,
              scale: visualViewport.scale,
              offsetTop: visualViewport.offsetTop,
              offsetLeft: visualViewport.offsetLeft,
              pageTop: visualViewport.pageTop,
              pageLeft: visualViewport.pageLeft,
            }
          : createEmptyMeasurements().visualViewport,
        devicePixelRatio: window.devicePixelRatio,
        maxTouchPoints: Number(navigator.maxTouchPoints) || 0,
        mediaQueries: {
          pointerCoarse: this.getMediaQueryResult("(pointer: coarse)"),
          pointerFine: this.getMediaQueryResult("(pointer: fine)"),
          hover: this.getMediaQueryResult("(hover: hover)"),
          noHover: this.getMediaQueryResult("(hover: none)"),
          portrait: this.getMediaQueryResult("(orientation: portrait)"),
          landscape: this.getMediaQueryResult("(orientation: landscape)"),
          fullscreen: this.getMediaQueryResult("(display-mode: fullscreen)"),
          standalone: this.getMediaQueryResult("(display-mode: standalone)"),
        },
        orientation: orientation
          ? {
              supported: true,
              type: orientation.type,
              angle: orientation.angle,
            }
          : createEmptyMeasurements().orientation,
        browser: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          onLine: Boolean(navigator.onLine),
        },
      };
    },
    getSummaryText() {
      const data = this.measurements;
      const lines = [
        "Diagnóstico de viewport",
        "",
        "Tela física",
        `screen.width: ${data.screen.width}`,
        `screen.height: ${data.screen.height}`,
        `screen.availWidth: ${data.screen.availWidth}`,
        `screen.availHeight: ${data.screen.availHeight}`,
        `screen.colorDepth: ${data.screen.colorDepth}`,
        "",
        "Viewport do navegador",
        `window.innerWidth: ${data.viewport.innerWidth}`,
        `window.innerHeight: ${data.viewport.innerHeight}`,
        `document.documentElement.clientWidth: ${data.viewport.clientWidth}`,
        `document.documentElement.clientHeight: ${data.viewport.clientHeight}`,
        "",
        "Largura de rolagem",
        `document.documentElement.scrollWidth: ${data.overflow.documentElement}`,
        `document.body.scrollWidth: ${data.overflow.body}`,
        `#app.scrollWidth: ${data.overflow.app}`,
        "",
        "Visual Viewport",
      ];

      if (data.visualViewport.supported) {
        lines.push(
          `visualViewport.width: ${data.visualViewport.width}`,
          `visualViewport.height: ${data.visualViewport.height}`,
          `visualViewport.scale: ${data.visualViewport.scale}`,
          `visualViewport.offsetTop: ${data.visualViewport.offsetTop}`,
          `visualViewport.offsetLeft: ${data.visualViewport.offsetLeft}`,
          `visualViewport.pageTop: ${data.visualViewport.pageTop}`,
          `visualViewport.pageLeft: ${data.visualViewport.pageLeft}`
        );
      } else {
        lines.push("Visual Viewport não suportada");
      }

      lines.push(
        "",
        "Dispositivo e escala",
        `window.devicePixelRatio: ${data.devicePixelRatio}`,
        `navigator.maxTouchPoints: ${data.maxTouchPoints}`,
        "",
        "Media queries",
        `(pointer: coarse): ${data.mediaQueries.pointerCoarse}`,
        `(pointer: fine): ${data.mediaQueries.pointerFine}`,
        `(hover: hover): ${data.mediaQueries.hover}`,
        `(hover: none): ${data.mediaQueries.noHover}`,
        `(orientation: portrait): ${data.mediaQueries.portrait}`,
        `(orientation: landscape): ${data.mediaQueries.landscape}`,
        `(display-mode: fullscreen): ${data.mediaQueries.fullscreen}`,
        `(display-mode: standalone): ${data.mediaQueries.standalone}`,
        "",
        "Orientação"
      );

      if (data.orientation.supported) {
        lines.push(
          `screen.orientation.type: ${data.orientation.type}`,
          `screen.orientation.angle: ${data.orientation.angle}`
        );
      } else {
        lines.push("screen.orientation não está disponível");
      }

      lines.push(
        "",
        "Navegador",
        `navigator.userAgent: ${data.browser.userAgent}`,
        `navigator.platform: ${data.browser.platform}`,
        `navigator.language: ${data.browser.language}`,
        `navigator.onLine: ${data.browser.onLine}`
      );

      return lines.join("\n");
    },
    async copyData() {
      const text = this.getSummaryText();
      let copied = false;

      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        try {
          await navigator.clipboard.writeText(text);
          copied = true;
        } catch (error) {
          copied = this.copyWithFallback(text);
        }
      } else {
        copied = this.copyWithFallback(text);
      }

      this.feedbackType = copied ? "success" : "error";
      this.feedbackMessage = copied
        ? "Dados copiados com sucesso."
        : "Não foi possível copiar os dados neste navegador.";
    },
    copyWithFallback(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.setAttribute("readonly", "readonly");
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      let copied = false;
      try {
        copied = Boolean(document.execCommand("copy"));
      } catch (error) {
        copied = false;
      }

      document.body.removeChild(textArea);
      return copied;
    },
    close() {
      const query = Object.assign({}, this.$route.query);
      delete query["viewport-debug"];

      const location = {
        path: this.$route.path,
        query,
      };

      if (this.$route.hash) {
        location.hash = this.$route.hash;
      }

      const navigation = this.$router.replace(location);
      if (navigation && typeof navigation.catch === "function") {
        navigation.catch(() => {});
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.viewport-debug-panel {
  position: fixed;
  top: 12px;
  right: 12px;
  right: max(12px, env(safe-area-inset-right));
  bottom: 12px;
  bottom: max(12px, env(safe-area-inset-bottom));
  z-index: 2147483000;
  display: flex;
  width: calc(100vw - 24px);
  max-width: 440px;
  max-height: calc(100vh - 24px);
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid #d9dee8;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 10px 32px rgba(15, 23, 42, 0.28);
  color: #1f2937;
  font-family: Arial, sans-serif;
  font-size: 14px;
  line-height: 1.35;
}

.viewport-debug-panel--minimized {
  top: auto;
  bottom: 12px;
  bottom: max(12px, env(safe-area-inset-bottom));
  width: auto;
  max-width: calc(100vw - 24px);
  max-height: none;
  border-radius: 10px;
}

.viewport-debug-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 14px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.viewport-debug-panel__title {
  margin: 0;
  color: #111827;
  font-size: 17px;
  font-weight: 700;
}

.viewport-debug-panel__subtitle {
  margin: 3px 0 0;
  color: #64748b;
  font-size: 12px;
}

.viewport-debug-panel__header-actions,
.viewport-debug-panel__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.viewport-debug-panel__header-actions {
  flex-shrink: 0;
}

.viewport-debug-panel__actions {
  flex-wrap: wrap;
  padding: 10px 14px;
  border-bottom: 1px solid #e5e7eb;
}

.viewport-debug-panel__button,
.viewport-debug-panel__icon-button {
  min-height: 44px;
  box-sizing: border-box;
  border: 1px solid #913a47;
  border-radius: 8px;
  background: #913a47;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  line-height: 1;
  padding: 10px 14px;
  touch-action: manipulation;
}

.viewport-debug-panel__button--secondary {
  border-color: #64748b;
  background: #ffffff;
  color: #334155;
}

.viewport-debug-panel__icon-button {
  width: 44px;
  padding: 0;
  font-size: 24px;
}

.viewport-debug-panel__button--expand {
  white-space: nowrap;
}

.viewport-debug-panel__button:focus,
.viewport-debug-panel__icon-button:focus {
  outline: 3px solid rgba(145, 58, 71, 0.3);
  outline-offset: 2px;
}

.viewport-debug-panel__feedback {
  margin: 0;
  padding: 8px 14px;
  font-size: 13px;
}

.viewport-debug-panel__feedback--success {
  background: #ecfdf5;
  color: #047857;
}

.viewport-debug-panel__feedback--error {
  background: #fef2f2;
  color: #b91c1c;
}

.viewport-debug-panel__body {
  min-height: 0;
  overflow-y: auto;
  padding: 10px 12px 14px;
  -webkit-overflow-scrolling: touch;
}

.viewport-debug-panel__section {
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.viewport-debug-panel__section:last-child {
  margin-bottom: 0;
}

.viewport-debug-panel__section h3 {
  margin: 0 0 8px;
  color: #334155;
  font-size: 14px;
  font-weight: 700;
}

.viewport-debug-panel__section dl {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 5px 10px;
  margin: 0;
}

.viewport-debug-panel__section dt {
  overflow-wrap: anywhere;
  color: #64748b;
}

.viewport-debug-panel__section dd {
  margin: 0;
  color: #111827;
  font-weight: 700;
  text-align: right;
  word-break: break-word;
}

.viewport-debug-panel__long-value {
  max-width: 220px;
  text-align: left !important;
}

.viewport-debug-panel__notice {
  margin: 0;
  color: #92400e;
  font-weight: 700;
}

@media (max-width: 480px) {
  .viewport-debug-panel {
    top: 8px;
    right: 8px;
    right: max(8px, env(safe-area-inset-right));
    bottom: 8px;
    bottom: max(8px, env(safe-area-inset-bottom));
    width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
  }

  .viewport-debug-panel__header,
  .viewport-debug-panel__actions {
    padding-left: 10px;
    padding-right: 10px;
  }

  .viewport-debug-panel__body {
    padding-left: 8px;
    padding-right: 8px;
  }
}

@media print {
  .viewport-debug-panel {
    display: none !important;
  }
}
</style>
